import type { ISingleSelectorProps } from '@repo/common/components/SingleSelector/SingleSelector';
import SingleSelector from '@repo/common/components/SingleSelector/SingleSelector';
import type { SelectablePropertyItem } from '@repo/common/components/SingleSelector/types';
import { integrationList } from '@repo/common/constants/integration/integrations';
import { useIntegrationList } from '../../../api';
import IntegrationLogo from '../../IntegrationLogo';

interface IntegrationSelectorProps
	extends Pick<ISingleSelectorProps, 'readOnly' | 'target' | 'withinPortal'> {
	onChange: (id: string) => void;
	initialValue?: string;
	allowEmptyOption?: boolean;
	withOnlyQueryableIntegrations?: boolean;
}

export function IntegrationSelector({
	onChange,
	initialValue,
	allowEmptyOption = false,
	withOnlyQueryableIntegrations = false,
	...singleSelectorProps
}: IntegrationSelectorProps) {
	const { data: integrations } = useIntegrationList({
		options: {
			select: (data) => data.results,
		},
	});

	const integrationOptions = [
		...(allowEmptyOption
			? [
					{
						label: 'All integrations',
						value: '',
						iconName: 'plugConnected',
					},
				]
			: []),
		...(integrations || [])
			.filter((integration) =>
				integrationList.find(
					(localIntegration) =>
						!withOnlyQueryableIntegrations ||
						(localIntegration.type === integration.type &&
							localIntegration.supportsQueryBlocks)
				)
			)
			.map((integration) => ({
				label: integration.name,
				value: integration.id,
				icon: <IntegrationLogo integrationType={integration.type} size={16} />,
			})),
	] as SelectablePropertyItem[];

	return (
		<SingleSelector
			variant="default"
			iconType="emoji"
			isViewerUser={false}
			searchable
			options={integrationOptions}
			onChange={(id) => onChange(id)}
			initialSelected={initialValue}
			property="integration"
			{...singleSelectorProps}
		/>
	);
}
